import React, { createContext, useContext, useMemo } from 'react'
import { Dialog, DialogProps } from '@material-ui/core'
import { createStyles, Theme, withStyles } from '@material-ui/core/styles'

interface YumDialogContextValue {
  titleId: string
  descriptionId: string
}

const YumDialogContext = createContext<YumDialogContextValue | null>(null)

export const useYumDialogContext = () => useContext(YumDialogContext)

let yumDialogIdCounter = 0

const YumDialog = withStyles((theme: Theme) =>
  createStyles({
    container: {
      //alignItems: 'flex-end',
      alignItems: 'center',

      /*'@media (min-width: 992px)': {
        alignItems: 'center',
      },*/

      marginLeft: '15px',
      marginRight: '15px',
    },
    paper: {
      //borderRadius: '24px 24px 0 0',
      paddingBottom: '32px',
      margin: 0,
      borderRadius: '24px',

      /*'@media (min-width: 992px)': {
        borderRadius: '24px',
      },*/

      /*/!* iOS Safari 11.2, Safari 11 *!/
      '@supports (bottom: constant(safe-area-inset-bottom))': {
        paddingBottom: 'calc(constant(safe-area-inset-bottom) + 16px)',
      },

      /!* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ *!/
      '@supports (bottom: env(safe-area-inset-bottom))': {
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
      },*/
    },
  }),
)(({ classes, PaperProps, alert, ...props }: DialogProps & { alert?: boolean }) => {
  // Generate a stable id so YumDialogTitle (via context) can apply it to the
  // title element and the dialog gets a matching aria-labelledby — satisfies
  // axe's aria-dialog-name rule for every dialog using these components.
  const titleId = useMemo(() => {
    yumDialogIdCounter += 1
    return `yum-dialog-title-${yumDialogIdCounter}`
  }, [])
  // Matching id for the body text so the dialog gets an aria-describedby and screen
  // readers announce the message (e.g. "Are you sure...?"), not just the title.
  const descriptionId = useMemo(() => {
    yumDialogIdCounter += 1
    return `yum-dialog-desc-${yumDialogIdCounter}`
  }, [])
  const contextValue = useMemo(() => ({ titleId, descriptionId }), [titleId, descriptionId])
  // MUI v4's TrapFocus bounces focus through the MuiDialog-container on Tab
  // wrap-around, creating a phantom tab stop. Cycle focus manually so Tab
  // moves directly between the first and last focusable elements inside the
  // dialog (per WAI-ARIA APG dialog pattern).
  const paperRef = React.useRef<HTMLDivElement>(null)

  // Make sure focus actually lands inside the dialog on open. Some triggers (e.g. a
  // menu item that closes its menu) can leave focus on the activating control, and
  // MUI's auto-focus doesn't always recover it. If focus isn't already inside, move
  // it to the dialog container — which also lets screen readers read the dialog's
  // name + description (WCAG 2.4.3 / 4.1.2). If MUI already placed focus inside
  // (e.g. an autoFocus input), we leave it alone.
  React.useEffect(() => {
    if (!props.open) return
    const id = setTimeout(() => {
      const paper = paperRef.current
      if (paper && !paper.contains(document.activeElement)) {
        paper.focus()
      }
    }, 0)
    return () => clearTimeout(id)
  }, [props.open])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !paperRef.current) return
    const focusables = paperRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
  return (
    <YumDialogContext.Provider value={contextValue}>
      <Dialog
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        classes={classes}
        // aria-modal tells SRs the content behind the dialog is inert (WCAG 4.1.2);
        // MUI v4 puts role="dialog" on the Paper, so the attribute belongs there.
        // For confirmations (alert), use role="alertdialog" so screen readers announce
        // the title + message automatically on open, even when focus lands on a button.
        PaperProps={
          {
            ...PaperProps,
            ref: paperRef,
            onKeyDown: handleKeyDown,
            'aria-modal': true,
            // Focusable container so we can move focus here on open (see effect above).
            tabIndex: -1,
            ...(alert ? { role: 'alertdialog' } : {}),
          } as any
        }
        {...props}
      />
    </YumDialogContext.Provider>
  )
})

export default YumDialog
